"use client";
import { createContext, Dispatch, useContext, useReducer } from "react";
import { Meeting, Race } from "./class/cms";
import { MenuTab, RaceNo } from "./class/common/base";
import { RaceSelectorData } from "@/app/ui/Common/RaceSelector";
import { isBeforeCutoff, setRouting } from "./Common";
import { formattedDate } from "./DayJS";

interface MyContextValue {
  isLoadingMeetings: boolean; // default true, set to false once main data is retrieved.
  isLoadingRaces: boolean; // default true, set to false once main data is retrieved.
  isChangingMeeting: boolean; // default true, set to false once main data is retrieved.
  season: string; // sitecore season, eg: 23_24
  selectedMeetingDate: Date;
  selectedMeetingVenueCode: string; // add for handling historial results
  selectedRaceNo: RaceNo;
  defaultRaceNo: RaceNo;
  selectedMeeting?: Meeting;
  selectedRace?: Race;
  selectedRaceFormGuideUrl?: string;
  raceSelectorData?: RaceSelectorData; // for historical results
  menuTab: MenuTab;
  formGuides: FormGuideData[];
  currentFixtureList: any[];
  racingCalendarConfig: any;
  bizDate: Date; // business date
  redirectPageOnLand: boolean; // control redirect logic of page [PRJ2555-2131], redirect=Y, or only have racedate info, -> page redirection
  redirectMtgOrRaceIfEmptyOnLand: boolean; // control redirect logic of race [PRJ2555-2131], eg: if meeting is empty, -> meeting + race redirection, if race is empty, -> race redirection 
  nextDayRace?: Date;
  dataSource: "qids" | "both" | undefined;
  assetPrefix: string; // for using asset under '/public' folder
  calendarDateSelected: boolean;
  commonMeetings: any,
  cmsMeetingsOnOrAfterBizDateByDate: any,
  upComRaceData: any,
  renderMobileRaceCardStatus: any
}

interface MyContextDispatchAction {
  type: "update"|"changeMtgDate";
  value: Partial<MyContextValue>;
}

interface MyContext {
  state: MyContextValue;
  dispatch: Dispatch<MyContextDispatchAction>;
}

const myContextReducer = (state: MyContextValue, action: MyContextDispatchAction): MyContextValue =>  {
  switch (action.type) {
    case 'update': {
      return {
        ...state, 
        ...action.value
      };
    }
    case 'changeMtgDate': {
      if (action.value.selectedMeetingDate && action.value.selectedMeetingDate.getTime() !== new Date(0).getTime()){
        const beforeCutoff = isBeforeCutoff(action.value.selectedMeetingDate);
        const redirectPage = true;
        const focus = beforeCutoff ? false : true;
        console.log('route 9 - changeMtgDate')
        setRouting(undefined, {'RaceDate': formattedDate(action.value.selectedMeetingDate,"YYYYMMDD"), 'Racecourse': '', 'RaceNo': '', 'focus': focus ? '' : 'Y', 'b_cid': ''}, state);
        return {
          ...state,
          ...action.value, //selectedMeetingDate
          isLoadingMeetings: true,
          isLoadingRaces: true,
          isChangingMeeting: true,
          season: '',
          selectedMeetingVenueCode: '',
          selectedRaceNo: new RaceNo(0),
          defaultRaceNo: new RaceNo(0),
          menuTab: new MenuTab(''),
          formGuides: [],
          redirectPageOnLand: redirectPage,
          redirectMtgOrRaceIfEmptyOnLand: true,
          calendarDateSelected: focus,
          ...(beforeCutoff && {dataSource: "qids"}), // only show result (data from QIDS) before cutoff date
        }
      }
    }
    default: {
      throw Error('Unknown action: ' + action);
    }
  }
}

export const MyContext = createContext<MyContext>({} as MyContext);

interface MyContextProviderProps {
  children?: React.ReactNode;
  assetPrefix?: string;
}

export function MyContextProvider({ children, assetPrefix }: MyContextProviderProps) {
  const initialContext: MyContextValue = {
    isLoadingMeetings: true,
    isLoadingRaces: true,
    isChangingMeeting: true,
    season: '',
    selectedMeetingDate: new Date(0),
    selectedMeetingVenueCode: '',
    selectedRaceNo: new RaceNo(0),
    defaultRaceNo: new RaceNo(0),
    menuTab: new MenuTab(''),
    formGuides: [],
    currentFixtureList: [],
    racingCalendarConfig: {},
    bizDate: new Date(0),
    redirectPageOnLand: false,
    redirectMtgOrRaceIfEmptyOnLand: false,
    assetPrefix: assetPrefix || '',
    calendarDateSelected: false,
    dataSource: undefined,
    commonMeetings: null,
    cmsMeetingsOnOrAfterBizDateByDate: null,
    upComRaceData: null,
    renderMobileRaceCardStatus: null
  };

  const [state, dispatch] = useReducer(
    myContextReducer,
    initialContext
  );

  const value: MyContext = {state, dispatch}

  return (
    <MyContext.Provider value={value}>
      {children}
    </MyContext.Provider>
  );
}

export function useMyContext() {
  return useContext(MyContext);
}
